import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import { ErrorBoundary } from './components/System/ErrorBoundary';

// Context Providers
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { CartProvider } from './context/CartContext';
import { CartUIProvider } from './context/CartUIContext';
import { ToastProvider } from './components/UI/Toast';

// Monitoring & Security
import { validateEnv } from './lib/env';

// 1. Validate environment variables
try {
  validateEnv();
} catch (error) {
  if (import.meta.env.PROD) {
    throw error;
  }

  console.error('Environment validation failed. Supabase-backed features may be disabled:', error);
}

const initializeMonitoring = async (): Promise<void> => {
  try {
    const [{ initSentry }, { initWebVitals }] = await Promise.all([
      import('./lib/sentry'),
      import('./lib/webVitals'),
    ]);

    initSentry();
    initWebVitals();
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('Monitoring initialization skipped:', error);
    }
  }
};

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <LanguageProvider>
              <CurrencyProvider>
                <CartProvider>
                  <CartUIProvider>
                    <ToastProvider>
                      <App />
                    </ToastProvider>
                  </CartUIProvider>
                </CartProvider>
              </CurrencyProvider>
            </LanguageProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

void initializeMonitoring();
